import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { IDR } from "@/lib/format";
import { inputClass } from "@/components/Brand";
import { onImgError } from "@/components/gallery/imgError";
import { safeFrameCode } from "@/lib/frameCode";

/** Live "n left" chips for the pre-paid package, so the client always sees what remains. */
const QuotaCounters = ({ includedQty, remainingQty }) => {
  const { t } = useI18n();
  const sizes = Object.keys(includedQty);
  if (sizes.length === 0) return null;

  return (
    <div className="mt-8 flex flex-wrap gap-3" data-testid="print-quota-badges">
      {sizes.map((size) => {
        const left = remainingQty[size] ?? 0;
        const spent = left === 0;
        return (
          <span key={size} data-testid={`quota-left-${size}`}
            className={`font-mono-accent text-[10px] tracking-[0.14em] uppercase border px-3 py-1.5 transition-colors ${
              spent ? "border-[#2A2A2A] text-[#707070]" : "border-[#D4AF37]/40 text-[#D4AF37]"
            }`}>
            {size} · {spent ? t("quota_spent") : t("n_left", { n: left })}
          </span>
        );
      })}
    </div>
  );
};

/** Step 03 — route prints against the pre-paid package, then unlock extras per size. */
export const PrintStep = ({
  data, selectedPhotos, prints, includedQty, remainingQty, extraCost,
  onZoom, onAddPrint, onRemovePrint, onUpdatePrint,
}) => {
  const { t } = useI18n();

  const sizeLabel = (size) => {
    const left = remainingQty[size];
    const price = data.pricing?.print_prices?.[size] || 0;
    if (left > 0) return `${size} · ${t("n_left", { n: left })}`;
    // No configured price: say so rather than implying it is free.
    return price > 0 ? `${size} · +${IDR(price)}` : `${size} · ${t("price_on_request")}`;
  };

  return (
    <>
      <p className="eyebrow mb-4">{t("step_prints")}</p>
      <h1 className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium">{t("prints_title")}</h1>
      <p className="mt-4 text-sm sm:text-base text-[#A8A8A8] max-w-xl leading-relaxed">{t("prints_body")}</p>
      <p className="mt-2 text-xs text-[#707070] max-w-xl" data-testid="prints-multi-hint">{t("prints_multi_hint")}</p>

      <QuotaCounters includedQty={includedQty} remainingQty={remainingQty} />

      <div className="mt-10 space-y-px bg-[#2A2A2A] border border-[#2A2A2A]" data-testid="print-selection-list">
        {selectedPhotos.map((photo) => {
          const rows = prints.filter((p) => p.file_id === photo.file_id);
          return (
            <div key={photo.file_id} className="bg-[#0B0B0B] p-5 flex flex-col sm:flex-row gap-5">
              <button onClick={() => onZoom(photo)} className="shrink-0">
                <img src={photo.thumb} alt={photo.name} onError={(e) => onImgError(e, photo)}
                  className="w-full sm:w-28 h-40 sm:h-28 object-cover border border-[#2A2A2A]" />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <span className="font-mono-accent text-xs">{photo.name}</span>
                  <button data-testid={`print-toggle-${photo.file_id}`} onClick={() => onAddPrint(photo.file_id)}
                    className={`shrink-0 inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase px-4 min-h-[44px] border transition-colors ${
                      rows.length ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#2A2A2A] hover:border-[#D4AF37]"
                    }`}>
                    <Plus size={11} /> {rows.length ? t("add_another_size") : t("add_print")}
                  </button>
                </div>
                {rows.map((row, n) => {
                  const sameSize = prints.filter((p) => p.size === row.size);
                  const before = sameSize
                    .slice(0, sameSize.indexOf(row))
                    .reduce((a, p) => a + Number(p.qty || 1), 0);
                  const includedLeft = Math.max(0, (includedQty[row.size] || 0) - before);
                  const qty = Math.max(1, Number(row.qty) || 1);
                  const extraCopies = Math.max(0, qty - includedLeft);
                  const unit = data.pricing?.print_prices?.[row.size] || 0;
                  return (
                    <div key={row.row_id} className="mt-4 border-t border-[#1E1E1E] pt-4" data-testid={`print-row-${photo.file_id}-${n}`}>
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-mono-accent text-[10px] tracking-[0.16em] uppercase text-[#707070]">
                          {t("print_row")} {n + 1}
                        </span>
                        <button data-testid={`print-remove-${photo.file_id}-${n}`} onClick={() => onRemovePrint(row.row_id)}
                          aria-label={t("remove_print")} className="text-[#5A5A5A] hover:text-[#8B0000] min-h-[44px] px-1">
                          <Trash2 size={13} />
                        </button>
                      </div>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-4">
                        <select data-testid={`print-size-select-${photo.file_id}-${n}`} value={row.size}
                          onChange={(e) => onUpdatePrint(row.row_id, "size", e.target.value)}
                          className="bg-[#050505] border border-[#2A2A2A] px-3 py-2.5 min-h-[44px] text-base sm:text-sm focus:border-[#D4AF37] focus:outline-none">
                          {data.print_sizes.map((s) => <option key={s} value={s} label={sizeLabel(s)} />)}
                        </select>
                        <div>
                          <input data-testid={`print-qty-input-${photo.file_id}-${n}`} type="number" min="1" max="99"
                            value={row.qty ?? 1}
                            onChange={(e) => onUpdatePrint(row.row_id, "qty", e.target.value)}
                            className={inputClass} aria-label={t("print_qty")} />
                          <span className="mt-1 block font-mono-accent text-[9px] tracking-[0.14em] uppercase text-[#707070]">
                            {t("print_qty")}
                          </span>
                        </div>
                        <input data-testid={`frame-code-input-${photo.file_id}-${n}`} value={row.frame_code}
                          maxLength={40}
                          onChange={(e) => onUpdatePrint(row.row_id, "frame_code", safeFrameCode(e.target.value))}
                          placeholder={t("frame_code")} className={inputClass} />
                        <input data-testid={`print-note-input-${photo.file_id}-${n}`} value={row.note}
                          onChange={(e) => onUpdatePrint(row.row_id, "note", e.target.value)}
                          placeholder={t("notes")} className={inputClass} />
                      </div>
                      <p className="mt-3 font-mono-accent text-[10px] tracking-[0.14em] uppercase" data-testid={`print-price-${photo.file_id}-${n}`}>
                        {includedLeft > 0 && (
                          <span className="text-[#707070]">
                            {Math.min(qty, includedLeft)} × {t("included")}
                          </span>
                        )}
                        {extraCopies > 0 && (
                          <span className="text-[#D4AF37]">
                            {includedLeft > 0 ? " · " : ""}
                            {unit > 0
                              ? `${extraCopies} × ${t("extra_charge")} ${IDR(extraCopies * unit)}`
                              : `${extraCopies} × ${t("extra_charge")} · ${t("price_on_request")}`}
                          </span>
                        )}
                        {row.frame_code && (
                          <span className="text-[#D4AF37]"> + {t("frames")} {IDR(qty * (data.pricing?.frame_price || 0))}</span>
                        )}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {extraCost > 0 && (
        <div className="mt-10 border-t border-[#D4AF37]/40 pt-5 flex items-center justify-between gap-4"
          data-testid="print-step-total">
          <span className="text-[11px] tracking-[0.18em] uppercase text-[#A8A8A8]">{t("running_total")}</span>
          <span className="font-mono-accent text-lg text-[#D4AF37]">{IDR(extraCost)}</span>
        </div>
      )}
    </>
  );
};
